import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Crown, User, UserPlus, X } from 'lucide-react';
import { api } from '../services/api';
import { getUserId } from '../services/auth';
import { LocationsEditor } from './LocationsEditor';
import { Button, Modal } from './index';
import type { FlatSearch } from '../types/api';

interface SearchSettingsProps {
  search: FlatSearch;
  onBack: () => void;
  onSearchUpdate: (search: FlatSearch) => void;
}

function SavedIndicator({ visible, label }: { visible: boolean; label: string }) {
  return (
    <span className={`settings-saved-indicator ${visible ? 'visible' : ''}`}>
      {label}
    </span>
  );
}

export function SearchSettings({ search, onBack, onSearchUpdate }: SearchSettingsProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(search.name);
  const [originalName, setOriginalName] = useState(search.name);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(search.inviteCode ?? null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    getUserId().then(setCurrentUserId);
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(debounceRef.current);
    };
  }, []);

  const saveName = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === originalName) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateSearch(search.id, { name: trimmed });
      setOriginalName(trimmed);
      onSearchUpdate(updated);
      setSaved(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Failed to update name');
    } finally {
      setSaving(false);
    }
  }, [search.id, originalName, onSearchUpdate]);

  const handleNameChange = (value: string) => {
    setName(value);
    setError(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveName(value), 500);
  };

  const handleNameBlur = () => {
    clearTimeout(debounceRef.current);
    saveName(name);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      clearTimeout(debounceRef.current);
      saveName(name);
    }
  };

  const generateInvite = useCallback(async () => {
    setInviteLoading(true);
    setInviteError(false);
    try {
      const result = await api.createInvite(search.id);
      setInviteCode(result.inviteCode);
      return true;
    } catch {
      setInviteError(true);
      return false;
    } finally {
      setInviteLoading(false);
    }
  }, [search.id]);

  const inviteUrl = inviteCode
    ? `${import.meta.env.VITE_WEB_URL}?invite=${inviteCode}`
    : '';

  const handleInviteClick = useCallback(async () => {
    if (!inviteCode) {
      const ok = await generateInvite();
      if (!ok) return;
    }
    setShowInviteModal(true);
  }, [inviteCode, generateInvite]);

  const copyLink = useCallback(async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  }, [inviteUrl]);

  const shareWhatsApp = useCallback(() => {
    if (!inviteUrl) return;
    const text = t('app.inviteSection.whatsappMessage', { url: inviteUrl });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }, [inviteUrl, t]);

  const shareEmail = useCallback(() => {
    if (!inviteUrl) return;
    const subject = t('app.inviteSection.emailSubject');
    const body = t('app.inviteSection.emailBody', { url: inviteUrl });
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  }, [inviteUrl, t]);

  const isCreator = currentUserId === search.createdBy;
  const memberToRemove = search.members.find((m) => m.userId === removingMemberId);

  const handleRemoveMember = async () => {
    if (!removingMemberId) return;
    setRemoveLoading(true);
    setRemoveError(null);
    try {
      const updated = await api.removeMember(search.id, removingMemberId);
      onSearchUpdate(updated);
      setRemovingMemberId(null);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <button className="btn btn-ghost btn-sm back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> {t('common.back')}
      </button>

      <h2 className="settings-heading">{t('app.searchSettings.heading')}</h2>

      {/* ─── Search Name ─── */}
      <section className="settings-card">
        <h3 className="settings-section-title">{t('app.searchSettings.searchNameSection')}</h3>
        <div className="settings-field-row">
          <label className="settings-field-label" htmlFor="search-name">{t('app.searchSettings.nameLabel')}</label>
          <div className="settings-name-input-wrap">
            <input
              id="search-name"
              className="input"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              placeholder={t('app.searchSettings.namePlaceholder')}
              disabled={saving}
            />
            <SavedIndicator visible={saved} label={t('app.searchSettings.saved')} />
            {error && <span className="settings-message error">{error}</span>}
          </div>
        </div>
      </section>

      {/* ─── Locations ─── */}
      <section className="settings-card">
        <h3 className="settings-section-title">{t('app.searchSettings.locationsSection')}</h3>
        <p className="settings-section-desc">
          {t('app.searchSettings.locationsDescription')}
        </p>
        <LocationsEditor searchId={search.id} standalone />
      </section>

      {/* ─── Members ─── */}
      <section className="settings-card">
        <h3 className="settings-section-title">{t('app.searchSettings.membersSection')}</h3>
        <p className="settings-section-desc">
          {t('app.searchSettings.membersDescription')}
        </p>
        <div className="members-list">
          {search.members.map((member) => {
            const isMemberCreator = member.userId === search.createdBy;
            const isCurrentUser = member.userId === currentUserId;
            return (
              <div key={member.userId} className="member-row">
                <div className="member-info">
                  <span className="member-name">{member.name}</span>
                  <div className="member-badges">
                    {isMemberCreator && (
                      <span className="member-badge member-badge-owner"><Crown size={12} /> {t('app.searchSettings.ownerBadge')}</span>
                    )}
                    {isCurrentUser && (
                      <span className="member-badge member-badge-you"><User size={12} /> {t('app.searchSettings.youBadge')}</span>
                    )}
                  </div>
                  <span className="member-joined">{t('app.searchSettings.joinedDate', { date: new Date(member.joinedAt).toLocaleDateString() })}</span>
                </div>
                {isCreator && !isMemberCreator && (
                  <button
                    className="btn btn-ghost btn-sm member-remove-btn"
                    onClick={() => setRemovingMemberId(member.userId)}
                    aria-label={`Remove ${member.name}`}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {search.status === 'active' && search.members.length < 2 && (
          <div className="settings-invite-action">
            <Button
              variant="primary"
              size="sm"
              onClick={handleInviteClick}
              disabled={inviteLoading}
            >
              <UserPlus size={14} />
              {inviteLoading ? t('app.inviteSection.generating') : inviteError ? t('app.inviteSection.failedRetry') : t('app.inviteSection.invitePartner')}
            </Button>
          </div>
        )}
      </section>

      {/* ─── Invite Modal ─── */}
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title={t('app.inviteSection.inviteModalTitle')}>
        {inviteCode ? (
          <div className="invite-modal-content">
            <p className="invite-modal-desc">{t('app.inviteSection.inviteDescription')}</p>
            <div className="invite-code-display">
              <code>{inviteUrl}</code>
            </div>
            <div className="invite-share-buttons">
              <Button onClick={copyLink} variant="primary" size="sm">
                {copied ? t('common.copied') : t('app.inviteSection.copyLink')}
              </Button>
              <Button onClick={shareWhatsApp} variant="secondary" size="sm">
                {t('app.inviteSection.whatsapp')}
              </Button>
              <Button onClick={shareEmail} variant="secondary" size="sm">
                {t('app.inviteSection.email')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="invite-modal-content">
            <p>{t('app.inviteSection.generatingLink')}</p>
          </div>
        )}
      </Modal>

      {/* ─── Remove Member Confirmation ─── */}
      <Modal isOpen={!!removingMemberId} onClose={() => { setRemovingMemberId(null); setRemoveError(null); }} title={t('app.searchSettings.removeMemberTitle')}>
        <p>{t('app.searchSettings.removeConfirmation', { name: memberToRemove?.name })}</p>
        {removeError && <p className="settings-message error">{removeError}</p>}
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={() => { setRemovingMemberId(null); setRemoveError(null); }} disabled={removeLoading}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" onClick={handleRemoveMember} disabled={removeLoading}>
            {removeLoading ? t('app.searchSettings.removing') : t('app.searchSettings.remove')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
